import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { CheckoutModeProvider } from "@/context/CheckoutModeContext";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/layout/Header";
import RouteFocus from "@/components/layout/RouteFocus";
import SkipLink from "@/components/layout/SkipLink";
import ErrorBoundary from "@/components/layout/ErrorBoundary";
import Footer from "@/components/layout/Footer";
import Home from "@/pages/Home";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";
import { STATIC_PAGES } from "@/pages/staticPages";
import RouteTitle from "@/components/layout/RouteTitle";
import { isPartnerHost, isPartnerPath, partnerEntry } from "@/lib/partnerHost";
import NativeBridge from "@/components/NativeBridge";
import OfflineNotice from "@/components/OfflineNotice";
import CookieConsent from "@/components/CookieConsent";
import GroupBubble from "@/components/group-trips/GroupBubble";
import ShareTargetBanner from "@/components/group-trips/ShareTargetBanner";
import { APP_TARGET, isNativeApp, openExternal } from "@/lib/nativeApp";
import AppTabBar, { tabBarHidden } from "@/components/app/AppTabBar";
import AppTransition from "@/components/app/AppTransition";
import SplashMark from "@/components/app/SplashMark";
import useTravellerUnread from "@/components/app/useTravellerUnread";
import { isAdmin } from "@/lib/adminAuth";
import { isAdminHost } from "@/lib/adminHost";
import { PageLoading, lazyPage, preloadWhenIdle, useIsPageLoading, useMarkPageLoading } from "@/lib/lazyPage";

// Only the home page and the site's frame come with the first download; every
// other page is fetched when it is first shown (it was all one 650 kB file,
// partner and admin dictionaries included, before a traveller saw anything).
// A traveller never downloads the admin dashboard or the listing wizards.
// Pages of one module share a chunk, and <Suspense> below shows a quiet
// status line meanwhile.
const Hotels = lazyPage(() => import("@/pages/Hotels"));
const HotelDetail = lazyPage(() => import("@/pages/HotelDetail"));
const StayBooking = lazyPage(() => import("@/pages/checkout/StayBooking"));
const Cars = lazyPage(() => import("@/pages/Cars"));
const CarDetail = lazyPage(() => import("@/pages/CarDetail"));
const Flights = lazyPage(() => import("@/pages/Flights"));
const FlightDetail = lazyPage(() => import("@/pages/FlightDetail"));
const PassengerDetails = lazyPage(() => import("@/pages/PassengerDetails"));
const Login = lazyPage(() => import("@/pages/Login"));
const Signup = lazyPage(() => import("@/pages/Signup"));
const BookingDetail = lazyPage(() => import("@/pages/BookingDetail"));
const BookingSuccess = lazyPage(() => import("@/pages/BookingSuccess"));
const MyBookings = lazyPage(() => import("@/pages/MyBookings"));
const AppProfile = lazyPage(() => import("@/pages/account/AppProfile"));
const AppMessages = lazyPage(() => import("@/pages/messages/AppMessages"));
const AppBookingChat = lazyPage(() => import("@/pages/messages/AppBookingChat"));
const ManageBooking = lazyPage(() => import("@/pages/ManageBooking"));
const Support = lazyPage(() => import("@/pages/Support"));
const SupportCase = lazyPage(() => import("@/pages/SupportCase"));
const SupportCases = lazyPage(() => import("@/pages/SupportCases"));
const StaticPage = lazyPage(() => import("@/pages/StaticPage"));
const ForgotPassword = lazyPage(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyPage(() => import("@/pages/ResetPassword"));
const Account = lazyPage(() => import("@/pages/Account"));
const GroupTrips = lazyPage(() => import("@/pages/GroupTrips"));
const GroupJoin = lazyPage(() => import("@/pages/GroupJoin"));
const TripGroupe = lazyPage(() => import("@/pages/tripgroupe/TripGroupe"));
const Attractions = lazyPage(() => import("@/pages/Attractions"));
const AirportTaxis = lazyPage(() => import("@/pages/AirportTaxis"));
const FlightHotel = lazyPage(() => import("@/pages/FlightHotel"));
const PartnersLanding = lazyPage(() => import("@/pages/partner/PartnersLanding"));
const PartnerRegister = lazyPage(() => import("@/pages/partner/PartnerRegister"));
const PartnerPayoutSetup = lazyPage(() => import("@/pages/partner/PartnerPayoutSetup"));
const PartnerShell = lazyPage(() => import("@/pages/partner/PartnerShell"));
const AdminAccessDenied = lazyPage(() => import("@/pages/admin/AdminAccessDenied"));
// The design-system gallery (/__ds), in development only: the production
// build drops this branch, so neither the route nor its chunk ships.
const DsGallery = process.env.NODE_ENV !== "production" ? lazyPage(() => import("@/components/ds/DsGallery")) : null;
// Where a search from the home page leads: fetched once the browser is idle.
const SEARCH_RESULTS = [Hotels, Flights, Cars];

// Each lazy page waits in its own boundary (lib/lazyPage). These outer ones
// are the last resort: each shell waits inside its own frame, so the header
// and the partner bar stay up, and the outermost covers a shell that is
// itself still downloading (the partner one). The footer waits for the page.
const PartnerDashboard = lazyPage(() => import("@/pages/partner/PartnerDashboard"));
const PropertyWizard = lazyPage(() => import("@/pages/partner/PropertyWizard"));
const VehicleWizard = lazyPage(() => import("@/pages/partner/VehicleWizard"));
const AttractionWizard = lazyPage(() => import("@/pages/partner/AttractionWizard"));
const ActivitySlots = lazyPage(() => import("@/pages/partner/ActivitySlots"));
const VehicleAvailability = lazyPage(() => import("@/pages/partner/VehicleAvailability"));
const TaxiWizard = lazyPage(() => import("@/pages/partner/TaxiWizard"));
const AdminPartners = lazyPage(() => import("@/pages/admin/AdminPartners"));
const AdminLayout = lazyPage(() => import("@/pages/admin/AdminLayout"));
const AdminOverview = lazyPage(() => import("@/pages/admin/AdminOverview"));
const AdminFinance = lazyPage(() => import("@/pages/admin/AdminFinance"));
const AdminSettings = lazyPage(() => import("@/pages/admin/AdminSettings"));
const AdminTeam = lazyPage(() => import("@/pages/admin/AdminTeam"));
const AdminAccount = lazyPage(() => import("@/pages/admin/AdminAccount"));
const AdminLogin = lazyPage(() => import("@/pages/admin/AdminLogin"));
const AdminRiskReview = lazyPage(() => import("@/pages/admin/RiskReview"));
const AdminReviews = lazyPage(() => import("@/pages/admin/AdminReviews"));
const AdminPricing = lazyPage(() => import("@/pages/admin/AdminPricing"));
const AdminRecommendations = lazyPage(() => import("@/pages/admin/AdminRecommendations"));
const AdminBookingDetail = lazyPage(() => import("@/pages/admin/AdminDetail").then((m) => ({ default: m.AdminBookingDetail })));
const AdminPropertyDetail = lazyPage(() => import("@/pages/admin/AdminDetail").then((m) => ({ default: m.AdminPropertyDetail })));
const AdminFlights = lazyPage(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminFlights })));
const AdminTicketing = lazyPage(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminTicketing })));
const AdminCars = lazyPage(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminCars })));
const AdminTaxis = lazyPage(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminTaxis })));
const AdminSupport = lazyPage(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminSupport })));
const AdminSupportCase = lazyPage(() => import("@/pages/admin/support/SupportCase"));
const AdminRisk = lazyPage(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminRisk })));
const AdminPayouts = lazyPage(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminPayouts })));
const AdminRefunds = lazyPage(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminRefunds })));
const AdminBookings = lazyPage(() => import("@/pages/admin/AdminPages").then((m) => ({ default: m.AdminBookings })));
const AdminAccommodations = lazyPage(() => import("@/pages/admin/AdminPages").then((m) => ({ default: m.AdminAccommodations })));
const AdminDocuments = lazyPage(() => import("@/pages/admin/AdminPages").then((m) => ({ default: m.AdminDocuments })));
const AdminCustomers = lazyPage(() => import("@/pages/admin/AdminPages").then((m) => ({ default: m.AdminCustomers })));
const AdminAudit = lazyPage(() => import("@/pages/admin/AdminPages").then((m) => ({ default: m.AdminAudit })));
const AdminPayments = lazyPage(() => import("@/pages/admin/AdminPages").then((m) => ({ default: m.AdminPayments })));

// The sign-in check is a page on its way too: the footer stays back.
function AuthPending() {
  useMarkPageLoading();
  return <div className="min-h-[60vh] flex items-center justify-center text-touki-muted">Loading…</div>;
}

// The footer comes in with the page, never ahead of it. Under a loading line
// it painted just below the header and then jumped a screen down (lib/lazyPage).
function SiteFooter() {
  return useIsPageLoading() ? null : <Footer />;
}

// The apps' screens slide from one to the next (owner, 2026-10-24: the
// approved Android design); the website renders its routes as they are.
function AppScreens({ children }) {
  return isNativeApp() ? <AppTransition>{children}</AppTransition> : children;
}

// The traveller app's tab bar, the Messages tab carrying the unread count.
function TravellerTabBar() {
  const unread = useTravellerUnread();
  return <AppTabBar unread={unread} />;
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthPending />;
  // Carry the destination through the sign-in. Login already knows how to
  // honour ?next; it was simply never told where the visitor was going, so
  // anyone following a link to /admin signed in and landed on the homepage
  // with no idea why, and had to find their way back by hand.
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  // Any admin role, not the single legacy "admin" string — the role model added
  // six more, and an exact match here would lock every one of them out of the
  // pages they are meant to run. The server checks the real permission anyway;
  // this only decides whether to render the route at all.
  // Signed in, but not with an admin role. Bouncing silently to the homepage
  // looks identical to the page being broken — which is exactly how it was
  // reported. Say what happened instead.
  //
  // A page, not a redirect. On the admin host "/" *is* the dashboard, so
  // sending them there would put this component straight back into itself —
  // Navigate to "/", ProtectedRoute, Navigate to "/", forever. Explaining also
  // beats relocating: being moved without a reason is what made this look
  // broken in the first place.
  if (adminOnly && !isAdmin(user)) return <AdminAccessDenied email={user.email} />;
  return children;
}

// Decided once per load, not per render: the hostname cannot change without a
// navigation, and re-reading it on every render would invite a component to
// disagree with the router about which application it is in.
const ADMIN_HOST = isAdminHost();
const PARTNER_HOST = isPartnerHost();

/**
 * The dashboard, on its own hostname and mounted at the root.
 *
 * A separate shell rather than a branch inside AppShell: the two are different
 * applications that happen to share a bundle, and letting both declare a "/"
 * route in one <Routes> is how one of them silently wins.
 */
function AdminApp() {
  return (
    <>
      <Routes>
        <Route path="/" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminOverview />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="bookings/:id" element={<AdminBookingDetail />} />
            <Route path="accommodations" element={<AdminAccommodations />} />
            <Route path="accommodations/:id" element={<AdminPropertyDetail />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="documents" element={<AdminDocuments />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="flights" element={<AdminFlights />} />
            <Route path="recommendations" element={<AdminRecommendations />} />
            <Route path="ticketing" element={<AdminTicketing />} />
            <Route path="cars" element={<AdminCars />} />
            <Route path="taxis" element={<AdminTaxis />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="support/:caseId" element={<AdminSupportCase />} />
            <Route path="risk" element={<AdminRisk />} />
            <Route path="risk-review" element={<AdminRiskReview />} />
            <Route path="payouts" element={<AdminPayouts />} />
            <Route path="refunds" element={<AdminRefunds />} />
            <Route path="account" element={<AdminAccount />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="audit" element={<AdminAudit />} />
          </Route>
        {/* The dashboard's own sign-in. The customer page offered to create an
            account, sign in with Google and unlock member deals — none of which
            belongs on an internal tool. There is deliberately no /signup here;
            accounts come from the Team page. */}
        <Route path="/login" element={<AdminLogin />} />
        {/* Anything else on this host is not a page: send it to the overview
            rather than to a customer site that is not served here. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="bottom-right" richColors />
    </>
  );
}

/**
 * The booking app never shows the partner space (owner, 2026-11-01): a
 * partner address reached inside it (a deep link, an old link, a
 * notification) opens the same page on partners.toukky.com in the phone's
 * browser, and the app goes back to its first screen.
 */
function PartnerSiteHandoff() {
  const { pathname, search } = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    openExternal(new URL(`${pathname}${search}`, partnerEntry).href).catch(() => {});
    nav("/", { replace: true });
  }, [pathname, search, nav]);
  return null;
}

function AppShell() {
  const location = useLocation();
  if (ADMIN_HOST) return <AdminApp />;
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const partnerChrome = PARTNER_HOST || isPartnerPath(location.pathname) ||
    (location.pathname === "/login" && isPartnerPath((new URLSearchParams(location.search).get("next") || "").split("?")[0]));
  if (partnerChrome && isNativeApp() && APP_TARGET === "customer") return <PartnerSiteHandoff />;
  if (partnerChrome) return (
    <PartnerShell>
      <RouteFocus />
      <RouteTitle />
      <Suspense fallback={<PageLoading />}>
      <AppScreens>
      <Routes>
        <Route path="/" element={<PartnersLanding />} />
        <Route path="/partners" element={<PartnersLanding />} />
        <Route path="/partners/register" element={<PartnerRegister />} />
        <Route path="/partners/register/payouts" element={<ProtectedRoute><PartnerPayoutSetup /></ProtectedRoute>} />
        <Route path="/login" element={<Login partnerPortal />} />
        {/* The sign-in page links here; without these the partner site answered 404. */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/support" element={<Support />} />
        <Route path="/support/cases" element={<ProtectedRoute><SupportCases /></ProtectedRoute>} />
        <Route path="/support/cases/:caseId" element={<SupportCase />} />
        {/* The partner app needs the account page (account deletion) and the legal pages too. */}
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        {Object.entries(STATIC_PAGES).map(([page, path]) => <Route key={page} path={path} element={<StaticPage page={page} />} />)}
        <Route path="/partner/dashboard" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
        <Route path="/partner/property/:id" element={<ProtectedRoute><PropertyWizard /></ProtectedRoute>} />
        <Route path="/partner/vehicle/:id" element={<ProtectedRoute><VehicleWizard /></ProtectedRoute>} />
        <Route path="/partner/attraction/:id" element={<ProtectedRoute><AttractionWizard /></ProtectedRoute>} />
        <Route path="/partner/attraction/:id/slots" element={<ProtectedRoute><ActivitySlots /></ProtectedRoute>} />
        <Route path="/partner/vehicle/:id/availability" element={<ProtectedRoute><VehicleAvailability /></ProtectedRoute>} />
        <Route path="/partner/taxi/:id" element={<ProtectedRoute><TaxiWizard /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </AppScreens>
      </Suspense>
      <Toaster position={isNativeApp() ? "bottom-center" : "bottom-right"} richColors />
    </PartnerShell>
  );
  // The admin control centre brings its own chrome — sidebar, header, its own
  // full-height layout. Wrapping it in the public marketing header and footer
  // as well would leave an operations tool with two headers and a legal
  // footer under it.
  const adminChrome = location.pathname === "/admin" || location.pathname.startsWith("/admin/");
  // The private conversation supplies its own compact header. List/join pages
  // and the creation form keep the public navigation; only a single group's
  // chat uses this shell.
  const groupChrome = /^\/groups\/(?!new\/?$)[^/]+\/?$/.test(location.pathname);
  // The traveller app: a tab bar instead of the footer and the group bubble
  // (its unread count lives on the Messages tab), the pages kept above it.
  const native = isNativeApp();
  const tabBarShown = native && !adminChrome && !tabBarHidden(location.pathname);

  return (
    <CheckoutModeProvider>
    <div className="min-h-screen flex flex-col bg-white">
      {!adminChrome && !groupChrome && <Header />}
      {!adminChrome && !groupChrome && <ShareTargetBanner />}
      <RouteFocus />
      <RouteTitle />
      <main id="main" tabIndex={-1} className={tabBarShown ? "flex-1 outline-none tk-has-tabbar" : "flex-1 outline-none"}>
        <Suspense fallback={<PageLoading />}>
        <AppScreens>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/hotels" element={<Hotels />} />
          <Route path="/hotels/:id" element={<HotelDetail />} />
          <Route path="/hotels/:id/book" element={<StayBooking />} />
          <Route path="/flight-hotel" element={<FlightHotel />} />
          <Route path="/cars" element={<Cars />} />
          <Route path="/cars/:id" element={<CarDetail />} />
          <Route path="/flights" element={<Flights />} />
          <Route path="/flights/:id" element={<FlightDetail />} />
          <Route path="/flights/:id/passengers" element={<PassengerDetails />} />
          <Route path="/attractions" element={<Attractions />} />
          <Route path="/airport-taxis" element={<AirportTaxis />} />
          <Route path="/list-property" element={<Navigate to="/partners" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* The apps' tabs answer signed out too (owner, 2026-10-26): Profil
              shows its options and the way in, Voyages and Messages theirs;
              the account's own page moves under Profil. */}
          <Route path="/account" element={native ? <AppProfile /> : <ProtectedRoute><Account /></ProtectedRoute>} />
          {native && <Route path="/account/details" element={<ProtectedRoute><Account /></ProtectedRoute>} />}
          {native && <Route path="/messages" element={<AppMessages />} />}
          {native && <Route path="/messages/booking/:id" element={<AppBookingChat />} />}
          <Route path="/bookings" element={native ? <MyBookings /> : <ProtectedRoute><MyBookings /></ProtectedRoute>} />
          <Route path="/groups" element={<ProtectedRoute><GroupTrips /></ProtectedRoute>} />
          {/* Creating a group: the traveller's details and the group's name on one form. */}
          <Route path="/groups/new" element={<ProtectedRoute><GroupTrips create /></ProtectedRoute>} />
          <Route path="/groups/:groupId" element={<ProtectedRoute><GroupTrips /></ProtectedRoute>} />
          <Route path="/groups/:groupId/join" element={<ProtectedRoute><GroupJoin /></ProtectedRoute>} />
          {/* The group trip's public presentation: outside /groups, whose
              pages need an account and a single group's drops the header. */}
          <Route path="/trip-groupe" element={<TripGroupe />} />
          <Route path="/booking/:id" element={<BookingDetail />} />
          <Route path="/booking/:id/success" element={<BookingSuccess />} />
          <Route path="/manage" element={<ManageBooking />} />
          <Route path="/support" element={<Support />} />
          {/* A guest opens their case with its token; the list is the signed-in customer's. */}
          <Route path="/support/cases" element={<ProtectedRoute><SupportCases /></ProtectedRoute>} />
          <Route path="/support/cases/:caseId" element={<SupportCase />} />
          {Object.entries(STATIC_PAGES).map(([page, path]) => <Route key={page} path={path} element={<StaticPage page={page} />} />)}
          {/* The control centre. Nested so the shell mounts once and every
              child renders inside it. The legacy single-page admin keeps its
              own route until its two forms are rebuilt inside the shell. */}
          {/* The dashboard is not mounted here — see AdminApp. */}
          <Route path="/admin/*" element={<Navigate to="/" replace />} />
          <Route path="/partners" element={<PartnersLanding />} />
          <Route path="/partners/register" element={<PartnerRegister />} />
          <Route path="/partners/register/payouts" element={<ProtectedRoute><PartnerPayoutSetup /></ProtectedRoute>} />
          <Route path="/partner/dashboard" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
          <Route path="/partner/property/:id" element={<ProtectedRoute><PropertyWizard /></ProtectedRoute>} />
          <Route path="/partner/vehicle/:id" element={<ProtectedRoute><VehicleWizard /></ProtectedRoute>} />
          <Route path="/partner/attraction/:id" element={<ProtectedRoute><AttractionWizard /></ProtectedRoute>} />
        <Route path="/partner/attraction/:id/slots" element={<ProtectedRoute><ActivitySlots /></ProtectedRoute>} />
        <Route path="/partner/vehicle/:id/availability" element={<ProtectedRoute><VehicleAvailability /></ProtectedRoute>} />
          <Route path="/partner/taxi/:id" element={<ProtectedRoute><TaxiWizard /></ProtectedRoute>} />
          {DsGallery ? <Route path="/__ds" element={<DsGallery />} /> : null}
          {/* Last, so it only catches what nothing else claimed. An unknown
              address used to render an empty <main>, which reads as a broken
              site rather than a wrong URL. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </AppScreens>
        </Suspense>
      </main>
      {!adminChrome && !groupChrome && !native && <SiteFooter />}
      {/* A member's group floats on every page (it hides itself inside the groups). */}
      {!adminChrome && !native && <GroupBubble />}
      {native && !adminChrome && <TravellerTabBar />}
      {native && <SplashMark />}
      <Toaster position={native ? "bottom-center" : "bottom-right"} richColors />
    </div>
    </CheckoutModeProvider>
  );
}

function App() {
  useEffect(() => (ADMIN_HOST ? undefined : preloadWhenIdle(SEARCH_RESULTS)), []);
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SettingsProvider>
          <AuthProvider>
            {/* First in the document, so it is the first Tab stop on every
                public and partner page. The cookie banner (fixed to the
                bottom) follows it and still comes before the header. The
                dashboard host has its own chrome and no #main. */}
            {!ADMIN_HOST && <SkipLink />}
            <NativeBridge />
            <OfflineNotice />
            {/* The apps ask nothing (owner, 2026-10-25): measurement starts
                only on an Accept, which they never ask for, so they keep the
                necessary storage only. */}
            {!isNativeApp() && <CookieConsent />}
            <Suspense fallback={<PageLoading />}>
              <AppShell />
            </Suspense>
          </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
